/*
 * The unified shell's search panel (PLT-7168), ported from the PLT-7118 prototype
 * (`views/internal/proto/internal-search.module.css`).
 *
 * The panel opens where the top bar's search field is: its own field lands on top of the bar's, and
 * it grows down and leftwards from there, so it reads as the field getting bigger rather than as a
 * dialog arriving. It keeps the shared dialog chrome (`Modal.module.css`'s radius, hairline, white
 * ground and shadow) because it blocks the page until Escape or a pick resolves it, but the dimming
 * behind it is light, not the modal's 0.6: the page stays legible around a panel anchored to it.
 */

.overlay {
  position: fixed;
  inset: 0;
  /* The step `Modal` and the shell's drawer take: above the global header (1000) and sticky page chrome. */
  z-index: 1100;
  background-color: oklch(from var(--color__black) l c h / 0.2);
  container: search-overlay / inline-size;
}

/*
 * Positioned off the bar field's measured top and right (`--anchor-top`, `--anchor-right`, set from
 * the trigger when the panel opens), less the panel's own `lg` padding, so the panel's field covers
 * the bar's. The panel never comes closer than `xs` to the window's edge. Without an anchor (the
 * field is in the phone drawer, or hidden) the fallbacks put it at the top right of the window.
 */
.panel {
  position: absolute;
  inset-block-start: max(var(--space__xs), calc(var(--anchor-top, var(--space__xs)) - var(--space__lg)));
  inset-inline-end: max(var(--space__xs), calc(var(--anchor-right, var(--space__xl)) - var(--space__lg)));
  display: flex;
  flex-direction: column;
  /* Room for a fund name and the facts that tell it from its neighbours, and the AI row's end label. */
  inline-size: min(40rem, calc(100% - 2 * var(--space__xs)));
  max-block-size: 80dvh;
  border-radius: var(--border-radius__xl);
  border: 1px solid var(--color__gray-300);
  background: var(--color__white);
  box-shadow: 0 2px 16px 0 oklch(var(--color__gray-950) / 0.25);
  overflow: hidden;
  container: search-panel / inline-size;
}

/* With team details on, wide enough for the description, the type phrase and a recognisable id. */
.panelWide {
  inline-size: min(48rem, calc(100% - 2 * var(--space__xs)));
}

/* Below `md` there is no bar field to grow from: the panel spans the window, `xs` from each edge. */
@container search-overlay (max-inline-size: 48rem) {
  .panel {
    inset-inline: var(--space__xs);
    inset-block-start: var(--space__xs);
    inline-size: auto;
  }
}

.dialog {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  outline: none;
}

.body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
  padding: var(--space__lg);
  border-block-end: 1px solid var(--color__gray-200);
}

/*
 * The type filter: single-select chips under the field. Unselected chips are the quiet outlined
 * control; the selected one takes the lilac selected-control fill with ink a dark step of the same
 * hue (DESIGN.md rules 5 and 22). Not the blue pill switcher, which switches between views of a
 * page; this narrows a list, and the row has to be able to wrap.
 */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space__sm);
}

.chip {
  display: inline-flex;
  align-items: center;
  block-size: 1.75rem;
  padding-inline: var(--space__md);
  border: 1px solid var(--color__gray-300);
  border-radius: var(--border-radius__full);
  background: var(--color__white);
  font-family: var(--font-family__primary);
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  line-height: var(--line-height__xs);
  color: var(--color__gray-800);
  cursor: pointer;
  outline: none;
  white-space: nowrap;
}

.chip[data-hovered] {
  background: var(--color__gray-100);
}

.chip[data-selected] {
  border-color: var(--color__lilac-300);
  background: var(--color__lilac-100);
  color: var(--color__lilac-900);
}

.chip[data-selected][data-hovered] {
  background: var(--color__lilac-200);
}

.chip[data-focus-visible] {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
}

/* The team's half of the row starts after a hairline, so the extra types read as an addition. */
.chipStartsTeamGroup {
  position: relative;
  margin-inline-start: var(--space__sm);
}

.chipStartsTeamGroup::before {
  content: '';
  position: absolute;
  /* Centred in the gap: the row's `sm` gap plus this chip's `sm` margin. */
  inset-inline-start: calc(-1 * var(--space__sm) - 0.5px);
  inset-block: var(--space__xs);
  border-inline-start: 1px solid var(--color__gray-300);
}

/*
 * The list scrolls; the field above and the key hints below stay. A hairline marks each pinned edge,
 * as the dialog chrome does for its own scrolling middle.
 */
.list {
  flex: 1 1 auto;
  min-block-size: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: var(--space__sm);
  outline: none;
}

/* More space between groups than within one: the region gap is twice the row gap. */
.section + .section {
  margin-block-start: var(--space__md);
}

.sectionHeader {
  padding: var(--space__xs) var(--space__sm);
}

/*
 * One result: the icon, then the name and its facts, then (on the AI row only) an end label. The
 * states are the design system's listbox row's (`Dropdowns.module.css`, `.item`): `gray-100` on hover
 * and on the keyboard's virtual focus, with the same inset radius, so a row here reads as a row there.
 */
.row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: baseline;
  column-gap: var(--space__sm);
  padding: var(--space__sm);
  border-radius: calc(var(--border-radius__lg) - var(--space__xs));
  cursor: pointer;
  outline: none;
}

.row[data-hovered],
.row[data-focused] {
  background: var(--color__gray-100);
}

/* The one focus ring's own tokens (`FocusRing.module.css`), inset so the panel's edge cannot clip it. */
.row[data-focus-visible] {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: calc(-1 * var(--focus-ring__width));
}

.icon {
  align-self: center;
  display: flex;
  color: var(--color__gray-500);
}

.svg {
  inline-size: 1rem;
  block-size: 1rem;
}

/*
 * The name wins the room: it does not shrink, so the facts after it give up every character before
 * the name loses one, and it truncates only when it alone is wider than the row. `md` between the
 * name and the facts, a step above the row's other gaps, because this seam separates two things.
 */
.main {
  display: flex;
  align-items: baseline;
  gap: var(--space__md);
  min-inline-size: 0;
  overflow-x: clip;
}

.main > :first-child {
  flex: 0 0 auto;
  min-inline-size: 0;
  max-inline-size: 100%;
}

.main > :last-child:not(:first-child) {
  flex: 0 1 auto;
  min-inline-size: 0;
}

.end {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  min-inline-size: 0;
  white-space: nowrap;
}

/* On a narrow panel the AI row's end label goes: the disc and "Ask Upright AI" already say what it does. */
@container search-panel (max-inline-size: 30rem) {
  .end:not(.teamFacts) {
    display: none;
  }
}

/*
 * The team's facts, right-aligned and capped so a long id cannot take the name's room. The type
 * phrase never wraps or shrinks (a two-line "High / quality" made a row taller than its
 * neighbours), so the id is what gives way.
 */
.teamFacts {
  gap: var(--space__md);
  max-inline-size: 18rem;
  overflow-x: clip;
}

.teamFacts > :not(:last-child) {
  flex: none;
}

.teamFacts > :last-child {
  min-inline-size: 0;
}

/*
 * Below the wide panel's full width the team's facts move under the name rather than squeezing it:
 * beside the id column a phone-width row had room for "Acme Indust…" and nothing else.
 */
@container search-panel (max-inline-size: 40rem) {
  .row:has(> .teamFacts) {
    grid-template-columns: auto minmax(0, 1fr);
    row-gap: var(--space__xs);
  }

  .teamFacts {
    grid-column: 2;
    justify-content: flex-start;
    max-inline-size: 100%;
  }
}

/*
 * The AI entry point at the end of a result list sits a step apart from the results, behind a
 * hairline: it is a way out of the search, not one more result.
 */
.askSectionLast {
  margin-block-start: var(--space__sm);
  padding-block-start: var(--space__sm);
  border-block-start: 1px solid var(--color__gray-200);
}

/* The AI entry point's mark: the top bar's Ask AI disc (`unified-top-bar.module.css`, `.sparkMark`), at row scale. */
.askMark {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: 1rem;
  block-size: 1rem;
  border-radius: var(--border-radius__full);
  background-color: var(--color__brand-yellow-loud);
  color: var(--color__gray-900);
}

.empty {
  padding: var(--space__lg) var(--space__sm);
}

.loading {
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
  padding: var(--space__md);
}

/* Wraps rather than squeezes: on a narrow panel the shortcut line drops under the key hints whole. */
.footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space__sm) var(--space__lg);
  white-space: nowrap;
  padding: var(--space__sm) var(--space__lg);
  border-block-start: 1px solid var(--color__gray-200);
}

.footerEnd {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space__sm) var(--space__lg);
}

.hints {
  display: flex;
  align-items: center;
  gap: var(--space__lg);
}

.hint {
  display: inline-flex;
  align-items: center;
  gap: var(--space__xs);
}

.key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-inline-size: 1.25rem;
  padding: 0 var(--space__xs);
  border: 1px solid var(--color__gray-300);
  border-radius: var(--border-radius__sm);
  background: var(--color__gray-100);
  font-family: var(--font-family__primary);
  font-size: var(--font-size__xs);
  line-height: var(--line-height__xs);
  color: var(--color__gray-700);
}

/*
 * A phone has no keyboard shortcuts to hint at, so the key hints and the shortcut go. A customer's
 * footer then has nothing left and goes too; the team's keeps its switch. Last in the file: these
 * selectors tie with the rules above on specificity, so source order decides.
 */
@container search-overlay (max-inline-size: 48rem) {
  .hints,
  .shortcut {
    display: none;
  }

  .footer:not(.footerTeam) {
    display: none;
  }
}
